/**
 * Finalization controls on the Review screen.
 *
 * Three panels, in the order a salesperson needs them:
 *
 * 1. **Readiness** — a percentage and two lists. The percentage is a usability
 *    indicator; the gate is separate and says so on screen, because a green
 *    number next to a refused Mark final would read as a bug.
 * 2. **Previous-client references** — shown only when there is something to
 *    show, with the section and the surrounding text so the fix is one click
 *    away rather than a hunt.
 * 3. **Document versions** — every generated document, never overwritten.
 */
import { useState } from 'react';
import type {
  FinalizationReport,
  LeakageFinding,
  ProposalVersionSummary,
  ReadinessItem,
  VersionValidationStatus,
} from '../../lib/api';
import type { StepId } from './steps';
import { Button, Card, Skeleton, formatDateTime } from '../../components/ui';

// ---------------------------------------------------------------- readiness

export function ReadinessPanel({
  report,
  onGoToStep,
}: {
  report: FinalizationReport | null;
  onGoToStep(step: StepId): void;
}) {
  if (!report) return <Skeleton className="h-40 w-full" />;

  const { readiness } = report;
  const tone =
    readiness.percent === 100 ? 'emerald' : readiness.percent >= 60 ? 'amber' : 'slate';

  return (
    <Card
      title="Proposal readiness"
      actions={
        <span
          className={[
            'text-[13px] font-semibold tabular-nums',
            tone === 'emerald' ? 'text-emerald-700' : 'text-ink',
          ].join(' ')}
        >
          {readiness.percent}%
        </span>
      }
    >
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-tint"
        role="progressbar"
        aria-valuenow={readiness.percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Proposal readiness"
      >
        <div
          className={[
            'h-full rounded-full transition-[width]',
            tone === 'emerald' ? 'bg-emerald-600' : tone === 'amber' ? 'bg-amber-500' : 'bg-teal',
          ].join(' ')}
          style={{ width: `${readiness.percent}%` }}
        />
      </div>

      <p className="mt-2 text-[12px] text-muted">
        A guide to how complete this proposal is. Critical checks still have to pass before it
        can be marked final.
      </p>

      <div className="mt-4 grid gap-5 sm:grid-cols-2">
        <ItemList
          heading="Completed"
          items={readiness.completed}
          empty="Nothing complete yet."
          marker="✓"
          markerClass="text-emerald-700"
          onGoToStep={onGoToStep}
        />
        <ItemList
          heading="Needs attention"
          items={readiness.needsAttention}
          empty="Nothing outstanding."
          marker="⚠"
          markerClass="text-amber-700"
          onGoToStep={onGoToStep}
        />
      </div>

      {report.reasons.length > 0 && (
        <div className="mt-5 border-t border-rule pt-4">
          <p className="text-[12px] font-semibold uppercase tracking-wide text-muted">
            Blocking finalization
          </p>
          <ul className="mt-2 space-y-1.5">
            {report.reasons.map((reason) => (
              <li key={reason} className="flex gap-2 text-[13px] text-amber-900">
                <span aria-hidden="true">•</span>
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function ItemList({
  heading,
  items,
  empty,
  marker,
  markerClass,
  onGoToStep,
}: {
  heading: string;
  items: ReadinessItem[];
  empty: string;
  marker: string;
  markerClass: string;
  onGoToStep(step: StepId): void;
}) {
  return (
    <div>
      <p className="text-[12px] font-semibold uppercase tracking-wide text-muted">
        {heading}
        {items.length > 0 && <span className="ml-1.5 font-normal">({items.length})</span>}
      </p>
      {items.length === 0 ? (
        <p className="mt-2 text-[13px] text-muted">{empty}</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {items.map((item, index) => (
            <li key={`${item.area}-${item.label}-${index}`} className="flex gap-2 text-[13px]">
              <span aria-hidden="true" className={markerClass}>
                {marker}
              </span>
              <span className="min-w-0">
                <span className="text-body">{item.label}</span>
                {(item.detail || item.attention) && (
                  <span className="block text-[12px] text-muted">
                    {item.attention ?? item.detail}
                  </span>
                )}
                {!item.complete && (
                  <button
                    type="button"
                    onClick={() => onGoToStep(item.step as StepId)}
                    className="mt-0.5 text-[12px] font-semibold text-teal underline"
                  >
                    Go to {item.step}
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ leakage

export function LeakagePanel({
  report,
  busy,
  onGoToStep,
  onAcknowledge,
}: {
  report: FinalizationReport | null;
  busy: boolean;
  onGoToStep(step: StepId): void;
  onAcknowledge(findingIds: string[]): void;
}) {
  const leakage = report?.leakage;
  // Nothing to show is the normal case; an empty panel would only add noise.
  if (!leakage || leakage.findings.length === 0) return null;

  const outstanding = leakage.outstanding;

  return (
    <section
      className={[
        'rounded-lg border p-5',
        outstanding.length > 0 ? 'border-amber-300 bg-amber-50' : 'border-rule bg-tint',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[15px] font-semibold text-ink">
          {outstanding.length > 0
            ? 'Potential previous-client reference detected.'
            : 'Previous-client references acknowledged'}
        </h2>
        {outstanding.length > 0 && (
          <Button
            variant="secondary"
            loading={busy}
            onClick={() => onAcknowledge(outstanding.map((f) => f.id))}
          >
            Acknowledge all
          </Button>
        )}
      </div>

      {leakage.originalClientName && (
        <p className="mt-1 text-[13px] text-amber-900">
          This proposal was duplicated from one for{' '}
          <strong>{leakage.originalClientName}</strong>. Correct the text below, or acknowledge
          it if it is intentional.
        </p>
      )}

      <ul className="mt-4 space-y-3">
        {leakage.findings.map((finding) => (
          <LeakageRow
            key={finding.id}
            finding={finding}
            busy={busy}
            onGoToStep={onGoToStep}
            onAcknowledge={onAcknowledge}
          />
        ))}
      </ul>
    </section>
  );
}

function LeakageRow({
  finding,
  busy,
  onGoToStep,
  onAcknowledge,
}: {
  finding: LeakageFinding;
  busy: boolean;
  onGoToStep(step: StepId): void;
  onAcknowledge(findingIds: string[]): void;
}) {
  return (
    <li className="rounded-md border border-rule bg-white px-3.5 py-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[13px] font-semibold text-ink">
          {finding.section}
          {finding.acknowledged && (
            <span className="ml-2 rounded-full bg-tint px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted">
              Acknowledged
            </span>
          )}
        </p>
        <p className="text-[12px] text-muted">
          matched “<span className="font-medium text-amber-900">{finding.term}</span>”
        </p>
      </div>
      <p className="mt-1.5 text-[13px] text-body">{finding.excerpt}</p>
      <div className="mt-2 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => onGoToStep(finding.step as StepId)}
          className="text-[12px] font-semibold text-teal underline"
        >
          Correct it
        </button>
        {!finding.acknowledged && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onAcknowledge([finding.id])}
            className="text-[12px] font-semibold text-muted underline disabled:opacity-50"
          >
            It is intentional — acknowledge
          </button>
        )}
      </div>
    </li>
  );
}

// ----------------------------------------------------------------- versions

export function VersionHistory({ versions }: { versions: ProposalVersionSummary[] }) {
  if (versions.length === 0) return null;

  return (
    <Card title="Document versions">
      <p className="text-[12px] text-muted">
        Every generated document is kept. Regenerating adds a version; it never replaces one.
      </p>
      <ul className="mt-3 divide-y divide-rule">
        {versions.map((version) => (
          <li key={version.version} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2.5">
            <span className="text-[13px] font-semibold text-ink">v{version.version}</span>
            <span className="text-[12px] text-muted">{formatDateTime(version.createdAt)}</span>
            <span className="text-[12px] text-muted">
              template {version.templateVersion}
              {version.documentBytes !== null &&
                ` · ${Math.round(version.documentBytes / 1024)} KB`}
            </span>
            <ValidationBadge status={version.validationStatus} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * A version's validation state.
 *
 * Three states, not two. A version generated before placeholder validation
 * existed carries no result at all, and labelling that "failed" would accuse a
 * document nothing ever examined.
 */
function ValidationBadge({ status }: { status: VersionValidationStatus | undefined }) {
  const [label, tone, title] = !status
    ? ['Not validated', 'bg-tint text-muted', 'Generated before placeholder validation existed']
    : status.status === 'PASSED'
      ? ['Validated', 'bg-emerald-100 text-emerald-800', undefined]
      : [
          'Failed validation',
          'bg-amber-100 text-amber-900',
          status.criticalTokens.length
            ? `Unresolved: ${status.criticalTokens.join(', ')}`
            : undefined,
        ];

  return (
    <span
      className={`ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${tone}`}
      title={title}
    >
      {label}
    </span>
  );
}

// ---------------------------------------------------------------- duplicate

const CARRY_OVER = [
  {
    key: 'retainScopeSelections',
    label: 'Scope, deliverables and approach selections',
    hint: 'What was included last time, ready to adjust.',
    commercial: false,
  },
  {
    key: 'retainDurations',
    label: 'Timeline durations',
    hint: 'Phase lengths from the source proposal.',
    commercial: false,
  },
  {
    key: 'retainCustomContent',
    label: 'Custom items and assumptions',
    hint: 'Anything added or reworded on the source. Check these for the old client’s name.',
    commercial: false,
  },
  {
    key: 'retainPricing',
    label: 'Pricing — amounts and discounts',
    hint: 'Off by default: a price agreed with one client is rarely the right price for another.',
    commercial: true,
  },
  {
    key: 'retainExecutiveSummary',
    label: 'Executive summary',
    hint: 'Off by default: it is written about the previous client.',
    commercial: false,
  },
] as const;

export type DuplicateChoices = Record<(typeof CARRY_OVER)[number]['key'], boolean>;

const DEFAULT_CHOICES: DuplicateChoices = {
  retainScopeSelections: true,
  retainDurations: true,
  retainCustomContent: true,
  retainPricing: false,
  retainExecutiveSummary: false,
};

/**
 * Asks what carries over before a duplicate is made.
 *
 * Pricing is the reason this dialog exists: carrying a negotiated figure into
 * another client's proposal silently is the expensive mistake, so it is opt-in
 * and labelled as commercial information.
 */
export function DuplicateDialog({
  proposalNumber,
  clientCompanyName,
  busy,
  onCancel,
  onConfirm,
}: {
  proposalNumber: string;
  clientCompanyName: string;
  busy: boolean;
  onCancel(): void;
  onConfirm(choices: DuplicateChoices): void;
}) {
  const [choices, setChoices] = useState<DuplicateChoices>(DEFAULT_CHOICES);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="duplicate-title"
    >
      <div className="max-h-full w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <h2 id="duplicate-title" className="text-[16px] font-semibold text-ink">
          Duplicate {proposalNumber}
        </h2>
        <p className="mt-1 text-[13px] text-muted">
          The new proposal starts as a draft with its own number. Client name, contacts and
          client-specific context are always cleared. Choose what carries over.
        </p>

        <ul className="mt-4 space-y-3">
          {CARRY_OVER.map((option) => (
            <li key={option.key}>
              <label className="flex gap-3">
                <input
                  type="checkbox"
                  className="mt-1 size-4 shrink-0 accent-teal"
                  checked={choices[option.key]}
                  onChange={(event) =>
                    setChoices((current) => ({
                      ...current,
                      [option.key]: event.target.checked,
                    }))
                  }
                />
                <span className="min-w-0">
                  <span className="block text-[13px] font-medium text-ink">
                    {option.label}
                    {option.commercial && (
                      <span className="ml-2 rounded-full bg-tint px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-teal">
                        Commercial
                      </span>
                    )}
                  </span>
                  <span className="block text-[12px] text-muted">{option.hint}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>

        {clientCompanyName && (
          <p className="mt-4 rounded-md bg-tint px-3.5 py-2.5 text-[12px] text-muted">
            Retained text will be checked for references to{' '}
            <strong className="text-ink">{clientCompanyName}</strong> before the new proposal can
            be generated.
          </p>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(choices)} loading={busy}>
            Create duplicate
          </Button>
        </div>
      </div>
    </div>
  );
}
